<script setup lang="ts">
import { useId } from 'vue';

type SelectProps = {
  modelValue: string | null;
  placeholder: string;
  label: string;
  options: Array<{ value: string; label: string }>;
};

defineProps<SelectProps>();
defineEmits(['update:modelValue']);

const inputId = useId();
</script>

<template>
  <div class="v-select-wrapper">
    <label
      :for="inputId"
      class="v-select-label"
    >
      {{ label }}
      <span v-if="!!$attrs.required" aria-hidden="true" class="required-star">*</span>
    </label>
    <select
      :id="inputId"
      :value="modelValue"
      class="v-select"
      @change="$emit('update:modelValue', ($event.target as HTMLSelectElement).value)"
    >
      <option v-for="opt in options" :key="opt.value" :value="opt.value">
        {{ opt.label }}
      </option>
      <slot />
    </select>
    <svg class="v-select-icon" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
      <polyline points="6 9 12 15 18 9" />
    </svg>
  </div>
</template>

<style scoped>
.v-select-wrapper {
  position: relative;
  width: 100%;
}

.v-select {
  display: flex;
  height: 2.5rem;
  width: 100%;
  border: 1px solid #d1d5db;
  background-color: white;
  border-radius: 0.375rem;
  padding: 0.5rem 0.75rem;
  font-size: 0.875rem;
  ring-offset-color: white;
  transition: all 0.2s;
  appearance: none;
  cursor: pointer;
}

.v-select-label {
  font-size: 0.95rem;
  font-weight: 500;
  color: #1f2937;
  text-align: left;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin-bottom: 0.5rem;
}

.v-select:focus-visible {
  outline: 2px solid transparent;
  outline-offset: 2px;
  box-shadow: 0 0 0 2px var(--yb-color-primary);
  border-color: #3b82f6;
}

.v-select-icon {
  position: absolute;
  right: 10px;
  top: 43px;
  width: 1rem;
  height: 1rem;
  z-index: 5;
  color: var(--yb-color-primary);
  pointer-events: none;
}

.required-star {
  color: var(--yb-color-error);
  margin-left: 0.25rem;
  margin-right: 0.5rem;
  font-weight: 700;
}
</style>
